import {
  featureDetails,
  featureDisplayState,
  isExcludedState,
  marketsExportLabel,
  visibleFeatures,
  type ProjectedFeature,
} from '@nexus/contracts';

/**
 * Minimal report shape for client-facing text/HTML narratives. Intentionally
 * omits brand, title, overall score, creator, and signed URLs (AD-1638).
 */
export interface TextExportAsset {
  id: string;
  originalName: string | null;
  status: string;
  score: number | null;
  band: string | null;
  error: string | null;
  features: ProjectedFeature[];
}

export interface TextExportReport {
  platform: string;
  placement: string;
  markets: Array<{ name: string; code: string }>;
  createdAt: string;
  assets: TextExportAsset[];
}

type Verdict = 'PASSED' | 'FAILED';
type HtmlTone = 'success' | 'warning' | 'error' | 'muted' | 'foreground';

interface FeatureSection {
  kind: 'WEIGHTED' | 'NOT_WEIGHTED';
  verdict: Verdict;
  heading: string;
  mixedWeights: boolean;
  features: ProjectedFeature[];
}

interface AssetNarrative {
  label: string;
  scoreLine: string | null;
  scoreBand: string | null;
  error: string | null;
  sections: FeatureSection[];
  scoreCalculation: string | null;
}

/**
 * Light-theme semantic hex from `packages/ui/src/design-system.css` (DESIGN.md).
 * Inline only — email/CMS clients do not resolve CSS variables or Tailwind classes.
 */
const HTML_TONE = {
  success: '#059669',
  warning: '#d97706',
  error: '#dc2626',
  muted: '#6b7280',
  foreground: '#111827',
} as const satisfies Record<HtmlTone, string>;

function htmlToneStyle(tone: HtmlTone): string {
  return `color:${HTML_TONE[tone]}`;
}

function displayTone(feature: ProjectedFeature): HtmlTone {
  const state = featureDisplayState(feature);
  if (state === 'PASS') return 'success';
  if (state === 'PARTIAL') return 'warning';
  if (state === 'FAIL') return 'error';
  return 'muted';
}

function bandHtmlTone(band: string | null): HtmlTone {
  if (band === 'STRONG') return 'success';
  if (band === 'NEEDS_REVIEW') return 'warning';
  if (band === 'AT_RISK') return 'error';
  return 'foreground';
}

const HTML_ESCAPE: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escape user-controlled strings before interpolating into HTML fragments. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => HTML_ESCAPE[ch] ?? ch);
}

/** Drop placeholder "N/A" fragments and de-duplicate before joining feature sub-checks. */
export function formatNotes(details: string[]): string {
  const cleaned = details
    .map((detail) => detail.trim())
    .filter((detail) => detail !== '' && detail.toUpperCase() !== 'N/A');
  return [...new Set(cleaned)].join(' · ');
}

/** SNAKE_CASE enum ("AT_RISK") → "Sentence case" ("At risk"). */
export function humanizeEnum(value: string): string {
  const lower = value.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Stable YYYY-MM-DD slice of an ISO timestamp (locale-independent). */
function isoDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

/** DS weight → 2-decimal percent label ("12.50%"), matching the UI table and PDF. */
function weightPct(weight: number): string {
  const pct = weight <= 1 ? weight * 100 : weight;
  return `${pct.toFixed(2)}%`;
}

/** "PASSED — 10% each" when every criterion shares a weight, else just "PASSED". */
function sectionHeading(label: string, features: ProjectedFeature[]): string {
  const weights = [...new Set(features.map((f) => f.weight))];
  const uniform = weights.length === 1 && (weights[0] ?? 0) > 0;
  return uniform ? `${label} — ${weightPct(weights[0]!)} each` : label;
}

/**
 * Client-safe heading label. Never falls back to a source URL, which can be
 * tokenized/signed and must not reach client users (AD-1638).
 */
function assetTextLabel(asset: TextExportAsset): string {
  return asset.originalName ?? `Asset ${asset.id}`;
}

function isPass(feature: ProjectedFeature): boolean {
  return featureDisplayState(feature) === 'PASS';
}

function isFail(feature: ProjectedFeature): boolean {
  const state = featureDisplayState(feature);
  return state === 'FAIL' || state === 'PARTIAL';
}

function featureLine(feature: ProjectedFeature, showWeight: boolean): string {
  const weight = showWeight ? ` (${weightPct(feature.weight)})` : '';
  const notes = formatNotes(featureDetails(feature));
  return `• ${feature.name}${weight}${notes ? ` — ${notes}` : ''}`;
}

function htmlFeatureItem(feature: ProjectedFeature, showWeight: boolean): string {
  const weight = showWeight ? ` (${escapeHtml(weightPct(feature.weight))})` : '';
  const notes = formatNotes(featureDetails(feature));
  const notesHtml = notes ? ` — ${escapeHtml(notes)}` : '';
  return `<li style="${htmlToneStyle(displayTone(feature))}">${escapeHtml(feature.name)}${weight}${notesHtml}</li>`;
}

function scoreLine(asset: TextExportAsset): string | null {
  if (asset.score != null) {
    return `Score: ${asset.score} / 100${asset.band ? ` (${humanizeEnum(asset.band)})` : ''}`;
  }
  return asset.band ? humanizeEnum(asset.band) : null;
}

function scoreCalculation(asset: TextExportAsset): string | null {
  const weightedPassed = asset.features.filter((f) => f.state === 'PASS');
  const weightedFailed = asset.features.filter(
    (f) => f.state === 'FAIL' || f.state === 'PARTIAL',
  );
  const weighted = [...weightedPassed, ...weightedFailed];
  const totalWeight = weighted.reduce((sum, f) => sum + f.weight, 0);
  if (weighted.length === 0 || asset.score == null) return null;
  const passedWeight = weightedPassed.reduce((sum, f) => sum + f.weight, 0);
  const pct = totalWeight > 0 ? Math.round((passedWeight / totalWeight) * 100) : 0;
  return `Score calculation: ${weightedPassed.length} of ${weighted.length} weighted criteria passed (${pct}%) → ${asset.score} / 100`;
}

function buildAssetNarrative(asset: TextExportAsset): AssetNarrative {
  const visible = visibleFeatures(asset.features);
  const weightedFeatures = visible.filter((f) => !isExcludedState(f.state));
  const notWeightedFeatures = visible.filter((f) => isExcludedState(f.state));

  const sections: FeatureSection[] = [];

  for (const [verdict, group] of [
    ['PASSED', weightedFeatures.filter(isPass)],
    ['FAILED', weightedFeatures.filter(isFail)],
  ] as const satisfies ReadonlyArray<readonly [Verdict, ProjectedFeature[]]>) {
    if (group.length === 0) continue;
    const mixedWeights = new Set(group.map((f) => f.weight)).size > 1;
    sections.push({
      kind: 'WEIGHTED',
      verdict,
      heading: sectionHeading(verdict, group),
      mixedWeights,
      features: group,
    });
  }

  for (const [verdict, group] of [
    ['PASSED', notWeightedFeatures.filter(isPass)],
    ['FAILED', notWeightedFeatures.filter(isFail)],
  ] as const satisfies ReadonlyArray<readonly [Verdict, ProjectedFeature[]]>) {
    if (group.length === 0) continue;
    sections.push({
      kind: 'NOT_WEIGHTED',
      verdict,
      heading: verdict,
      mixedWeights: false,
      features: group,
    });
  }

  return {
    label: assetTextLabel(asset),
    scoreLine: scoreLine(asset),
    scoreBand: asset.band,
    error: asset.error,
    sections,
    scoreCalculation: scoreCalculation(asset),
  };
}

function renderAssetText(narrative: AssetNarrative, multiAsset: boolean): string {
  const lines: string[] = [];
  if (multiAsset) lines.push(`── ${narrative.label} ──`);
  if (narrative.scoreLine) lines.push(narrative.scoreLine);
  if (narrative.error) lines.push(`Error: ${narrative.error}`);

  for (const section of narrative.sections) {
    const prefix = section.kind === 'WEIGHTED' ? 'WEIGHTED' : 'NOT WEIGHTED';
    lines.push('', `${prefix} — ${section.heading}`);
    for (const feature of section.features) {
      lines.push(featureLine(feature, section.mixedWeights));
    }
  }

  if (narrative.scoreCalculation) {
    lines.push('', narrative.scoreCalculation);
  }

  return lines.join('\n').trimEnd();
}

function renderAssetHtml(narrative: AssetNarrative, multiAsset: boolean): string {
  const parts: string[] = [];
  if (multiAsset) {
    parts.push(
      `<h2 style="${htmlToneStyle('foreground')}">${escapeHtml(narrative.label)}</h2>`,
    );
  }
  if (narrative.scoreLine) {
    parts.push(
      `<p><strong style="${htmlToneStyle(bandHtmlTone(narrative.scoreBand))}">${escapeHtml(narrative.scoreLine)}</strong></p>`,
    );
  }
  if (narrative.error) {
    parts.push(
      `<p style="${htmlToneStyle('error')}"><strong>Error:</strong> ${escapeHtml(narrative.error)}</p>`,
    );
  }

  for (const section of narrative.sections) {
    const prefix = section.kind === 'WEIGHTED' ? 'WEIGHTED' : 'NOT WEIGHTED';
    const headingTone: HtmlTone = section.verdict === 'PASSED' ? 'success' : 'error';
    parts.push(
      `<h3 style="${htmlToneStyle(headingTone)}">${escapeHtml(`${prefix} — ${section.heading}`)}</h3>`,
    );
    parts.push(
      `<ul>${section.features.map((f) => htmlFeatureItem(f, section.mixedWeights)).join('')}</ul>`,
    );
  }

  if (narrative.scoreCalculation) {
    parts.push(
      `<p><em style="${htmlToneStyle('muted')}">${escapeHtml(narrative.scoreCalculation)}</em></p>`,
    );
  }

  return parts.join('');
}

function reportHeaderLines(report: TextExportReport): string[] {
  return [
    `Platform: ${report.platform}`,
    `Placement: ${report.placement}`,
    `Market: ${marketsExportLabel(report.markets)}`,
    `Created: ${isoDate(report.createdAt)}`,
  ];
}

function reportHeaderHtml(report: TextExportReport): string {
  const rows: Array<[string, string]> = [
    ['Platform', report.platform],
    ['Placement', report.placement],
    ['Market', marketsExportLabel(report.markets)],
    ['Created', isoDate(report.createdAt)],
  ];
  const body = rows
    .map(([dt, dd]) => `<dt>${escapeHtml(dt)}</dt><dd>${escapeHtml(dd)}</dd>`)
    .join('');
  return `<dl>${body}</dl>`;
}

/**
 * Project the report down to a single asset, recomputing report-level aggregates
 * so a per-asset export's summary reads correctly.
 */
export function singleAssetReport<T extends TextExportReport>(
  report: T,
  asset: T['assets'][number],
): T {
  const criteriaPassedCount = asset.features.filter((f) => f.state === 'PASS').length;
  const criteriaFailedCount = asset.features.filter(
    (f) => f.state === 'FAIL' || f.state === 'PARTIAL',
  ).length;
  return {
    ...report,
    overallScore: asset.score,
    overallBand: asset.band,
    assetCount: 1,
    scoredCount: asset.status === 'SCORED' ? 1 : 0,
    failedCount: asset.status === 'FAILED' ? 1 : 0,
    criteriaPassedCount,
    criteriaFailedCount,
    assets: [asset],
  } as T;
}

/**
 * Build the plain-text narrative for client users. Header (Platform / Placement /
 * Market / Created), then one block per asset. Internal/sensitive fields and
 * signed asset links are omitted (AD-1638). Hidden no-verdict criteria match
 * the UI (AD-1749).
 */
export function buildReportText(report: TextExportReport): string {
  const multiAsset = report.assets.length > 1;
  const assetBlocks = report.assets.map((asset) =>
    renderAssetText(buildAssetNarrative(asset), multiAsset),
  );
  return [reportHeaderLines(report).join('\n'), ...assetBlocks].join('\n\n');
}

/** Semantic HTML fragment with the same narrative as {@link buildReportText}. */
export function buildReportHtml(report: TextExportReport): string {
  const multiAsset = report.assets.length > 1;
  const assetBlocks = report.assets.map((asset) =>
    renderAssetHtml(buildAssetNarrative(asset), multiAsset),
  );
  return [reportHeaderHtml(report), ...assetBlocks].join('');
}

/** Plain-text narrative for one asset — same content as the Copy-as-text button. */
export function assetToText(report: TextExportReport, asset: TextExportAsset): string {
  return buildReportText(singleAssetReport(report, asset));
}

/** HTML fragment for one asset — same narrative as {@link assetToText}. */
export function assetToHtml(report: TextExportReport, asset: TextExportAsset): string {
  return buildReportHtml(singleAssetReport(report, asset));
}
